import { computed } from 'vue'
import { cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import type { FormFieldValidationState, FormStateProps } from './types'

const borderedBoxVariants = cva('border-1 border-primary px-2 rounded-1 outline-none bg-white', {
    variants: {
        state: {
            default: 'border-primary focus-within:border-focus',
            error: 'border-error',
            success: 'border-success',
            warning: 'border-warning',
            missing: 'border-missing',
            disabled: 'bg-disabled border-primary',
            readonly: 'border-none px-0 ',
        },
    },
})

const textColorVariants = cva('', {
    variants: {
        state: {
            default: 'text-primary',
            error: 'text-primary',
            success: 'text-primary',
            warning: 'text-primary',
            missing: 'text-primary',
            disabled: 'text-disabled',
            readonly: 'text-primary',
        },
    },
})

const messageColorVariants = cva('', {
    variants: {
        state: {
            default: 'text-primary',
            error: 'text-error-primary',
            success: 'text-success-primary',
            warning: 'text-warning-primary',
            missing: 'text-missing-primary',
            disabled: 'text-primary',
            readonly: 'text-primary',
        },
    },
})

export const useFieldStateStyles = (props: FormStateProps) => {
    const validationState = computed(() => {
        if (props.error) {
            return 'error'
        }
        if (props.success) {
            return 'success'
        }
        if (props.warning) {
            return 'warning'
        }
        if (props.missing) {
            return 'missing'
        }
        if (props.disabled) {
            return 'disabled'
        }
        if (props.readonly) {
            return 'readonly'
        }
        return 'default'
    })

    const validationMarker = computed<FormFieldValidationState | null>(() => {
        const validationStates: FormFieldValidationState[] = ['error', 'warning', 'missing', 'success']
        const state = validationState.value as FormFieldValidationState
        return validationStates.includes(state) ? state : null
    })

    const borderBoxClass = computed(() => borderedBoxVariants({ state: validationState.value }))
    const textClass = computed(() => textColorVariants({ state: validationState.value }))
    const messageClass = computed(() => messageColorVariants({ state: validationState.value }))
    const rootClass = computed(() => twMerge(borderBoxClass.value, textClass.value))

    return {
        rootClass,
        borderBoxClass,
        textClass,
        messageClass,
        validationMarker,
    }
}
